<template>
  <DevBanner v-if="!isProduction" ref="devBanner" class="sticky top-0 z-50" />
  <RouterViewer />
  <IdleModal v-if="userStore.username && !userStore.isWebshop" />
  <ImpersonationIndicator />
  <Toasts />
  <StickyPhoneWidget />
  <CookieConsent />
  <CartDrawer v-if="!isB2C" />
  <ReservationExpiredNotice />
</template>
<script setup lang="ts">
import { DevBanner } from "@xs2event/js-lib/components";
import { isProduction } from "@/config";
import { IdleModal } from "@/components/sign-in";
import { inject, useTemplateRef, watch } from "vue";
import { useCssVar, useElementSize } from "@vueuse/core";
import { useUserStore } from "@/store/user";
import brandconfig, { isB2C } from "@/brandconfig";
import { ImpersonationIndicator } from "@xs2event/js-lib/impersonation";
import { Toasts } from "@xs2event/js-lib/toast";
import { useVersionChecker } from "@xs2event/js-lib/util";
import "@xs2event/js-lib/style";
import { api } from "@/helpers/api";
import RouterViewer from "@/pages/RouterViewer.vue";
import StickyPhoneWidget from "@/components/page/StickyPhoneWidget.vue";
import CookieConsent from "@/components/shared/CookieConsent.vue";
import CartDrawer from "@/components/cart/CartDrawer.vue";
import ReservationExpiredNotice from "@/components/cart/ReservationExpiredNotice.vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useQueryClient } from "@tanstack/vue-query";
import { prefetchPortalEventsCache } from "@/composables/events";
import { prefetchTeamsCatalog } from "@/composables/teams";
import { useDistributorStore } from "@/store/distributor";

const userStore = useUserStore();

// Sticky elements below the banner (the page header) offset by this. Border-box, so the banner's
// padding counts too.
const { height: devBannerHeight } = useElementSize(
  useTemplateRef("devBanner"),
  { width: 0, height: 0 },
  { box: "border-box" }
);
const devBannerHeightVar = useCssVar("--dev-banner-height", document.documentElement);
watch(devBannerHeight, (h) => (devBannerHeightVar.value = `${h}px`), { immediate: true });
const router = useRouter();

useVersionChecker({
  api,
  projectSlug: "xs2event-portal-v2",
  router,
});

userStore.init();

const { locale } = useI18n();
const queryClient = useQueryClient();
const distributorStore = useDistributorStore();

// Warm the (large, unpaginated) portal events cache as soon as a session exists, so opening an
// events listing renders from cache instead of paying the request latency at click time.
// `client_id` is set by handleAuthentication(), so this covers both entry points: session restore
// via login/refresh on page load, and an interactive sign-in. It is deliberately not
// `authCompleted`, which also resolves for anonymous visitors ("no refreshToken"). The cache is
// keyed on locale, so a language switch re-warms it. Issue #2884.
watch(
  [() => userStore.client_id, locale],
  ([clientId, language]) => {
    if (!clientId) return;
    prefetchPortalEventsCache(queryClient, { acceptLanguage: language });
    // Warmed alongside the events cache because the events listing's sport-type facet reads it, and
    // it is memoised by `api.cache` for the page load — so the listing's own call resolves from
    // memory instead of adding a request the user waits on.
    distributorStore.fetchDistributor(userStore.distributor_id).catch((err) => console.error(err));
    // Same idea for the full teams catalog (`/teams?page_size=9999`): it backs the events listing's
    // team filter facet (`Team.vue`) and is read by the router guard that resolves a
    // `?team=`/`?tournament=` breadcrumb (`src/pages/index.ts`).
    prefetchTeamsCatalog(queryClient);
  },
  { immediate: true }
);

if (brandconfig.GA_ID) {
  inject("gtag");
}
</script>
